import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert, CircularProgress, useTheme } from '@mui/material';
import { LinkOff as UnlinkIcon } from '@mui/icons-material';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import ReceiptCard from './ReceiptCard';
import InvoiceCard from './InvoiceCard';

interface ProofViewDialogProps {
  open: boolean;
  onClose: () => void;
  proof: any | null;
  onChanged: () => void;
  onDeleteRequested: (invoiceId: string) => void;
  /** True for a confirmed, unlinked ("self-reported") receipt/invoice shown via the "Cash /
   * manual entries" pseudo-account in BankConnectionDetailDialog — there's no transaction link
   * to remove, so the "Unlink from this transaction" action is hidden; the embedded card's own
   * Delete icon is the correct way to remove it instead. */
  isCash?: boolean;
}

// A linked proof is edited exactly the same way a standalone Invoice/Receipt is — this just
// wraps the existing ReceiptCard/InvoiceCard in a popup rather than building a second editor,
// so click-to-edit merchant, item include/exclude, rescan, and mismatch flagging all come for
// free. "Unlink" permanently deletes the record (row, items, stored file) along with the link —
// per direct instruction (2026-08-12): a proof upload is disposable in this workflow, and the
// previous "keep the document, just clear the link" design produced confusing content_hash
// "already uploaded" 409s when re-attaching the same file to a different transaction.
const ProofViewDialog: React.FC<ProofViewDialogProps> = ({ open, onClose, proof, onChanged, onDeleteRequested, isCash }) => {
  const { palette } = useTheme();
  const { token } = useAuth();
  const [unlinking, setUnlinking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);

  if (!proof) return null;

  const handleUnlink = async () => {
    setUnlinking(true);
    setUnlinkError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/invoices/${proof.id}/unlink-proof`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to delete this proof (${res.status})`);
      }
      onChanged();
      setConfirmOpen(false);
      onClose();
    } catch (err: any) {
      setUnlinkError(err.message || 'Failed to delete this proof');
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {proof.document_type === 'Receipt' ? 'Receipt' : 'Invoice'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ '& .MuiCard-root': { boxShadow: 'none', border: `1px solid ${palette.tokens.hairline}` } }}>
          {proof.document_type === 'Receipt' ? (
            <ReceiptCard
              invoice={proof}
              onUpdate={() => { onChanged(); onClose(); }}
              onDelete={(id) => { onClose(); onDeleteRequested(id); }}
            />
          ) : (
            <InvoiceCard
              invoice={proof}
              onUpdate={() => { onChanged(); onClose(); }}
              onDelete={(id) => { onClose(); onDeleteRequested(id); }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!isCash && (
          <Button
            startIcon={<UnlinkIcon />}
            onClick={() => { setUnlinkError(null); setConfirmOpen(true); }}
            sx={{ textTransform: 'none', color: palette.tokens.muted, mr: 'auto' }}
          >
            Unlink from this transaction
          </Button>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>

      <Dialog open={confirmOpen} onClose={() => !unlinking && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Unlink and delete this proof?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes it from the transaction and permanently deletes the {proof.document_type === 'Receipt' ? 'receipt' : 'invoice'} itself — the file, its details, and its line items. This can't be undone.
          </Typography>
          {unlinkError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setUnlinkError(null)}>
              {unlinkError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={unlinking} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleUnlink}
            disabled={unlinking}
            startIcon={unlinking ? <CircularProgress size={16} /> : undefined}
            sx={{ textTransform: 'none' }}
          >
            {unlinking ? 'Deleting…' : 'Unlink and delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ProofViewDialog;
